package com.pincer.app.viewmodel

import com.pincer.app.ApplicationState
import com.pincer.app.I18n
import com.pincer.client.Changes
import com.pincer.app.viewmodel.ShowAs.Problem
import com.pincer.app.viewmodel.ShowAs.Loading
import com.pincer.app.viewmodel.ShowAs.HelpText
import com.pincer.core.functions.validatePrincipalName
import com.pincer.core.functions.validateCredential
import com.pincer.core.functions.validateCode

fun app(state: ApplicationState): Node = root {
    if (state.transient.unexpectedError != null) {
        problem("unexpectedError") {
            node("details", state.transient.unexpectedError.toString())
        }
    }
    else if (state.remote == null) {
        loading("loading")
    }
    else if (state.remote!!.anyPacksFailed()) {
        problem("backgroundFailed")
    }
    else if (state.remote!!.principalId == null) {
        // Logged out
        getStarted(state, this)
    }
    else if (!state.personalIsReady()) {
        loading("loading")
    }
    else {

        summaries(state, this)

        pincerDetail(state, this)

    }
    appSettings(state, this)
}

/*
A node that opens on request (see Node.open): open or closed at first, as the view model chooses, and then as the
person leaves it. While it's closed, `init` isn't run.
*/
fun Node.opening(
    state:      ApplicationState,
    key:        String,
    atFirst:    Boolean,
    wording:    String = I18n.translate(key),
    summary:    String? = null,
    prominence: Prominence = Prominence.Normal,
    init:       Init = {},
) = node(key = key, wording = wording, summary = summary, prominence = prominence, open = state.isOpen(compoundKeyOf(key), atFirst), init = init)

/*
Reading vs changing. A field is something saved that people read, and some of them can change: a pincer's name, an
answer, a rule. The node's wording is the label, and it always has:

- `value`: what's saved, as text (or "Not set"), for everyone who can read it
- `change`, if this person can change it: a node that opens on request, holding the control and its problem and
  help text. It's open at first when nothing's saved yet (there's nothing to read, so offer the box) or when what was
  typed has a problem (so the problem is seen)

So a renderer can show "Pincer Name: Birdwatching" with a way to change it, an agent can read the value without
meeting a form, and the shape is the same whether or not the person can change it. Selects and checkboxes don't need
this: the control shows its value already. Forms to fill in (signing in, a new message, a new question) aren't fields:
nothing is saved yet. See docs/design_notes/clients.md.
*/
fun Node.field(
    state:      ApplicationState,
    key:        String,
    value:      String?,
    wording:    String = I18n.translate(key),
    problem:    String? = null,
    prominence: Prominence = Prominence.Normal,
    change:     Init? = null,
) = node(key = key, wording = wording, prominence = prominence) {
    node(key = "value", wording = if (value.isNullOrBlank()) I18n.translate("notSet") else value)
    if (change != null) {
        opening(state = state, key = "change", wording = I18n.translate("change-field", wording), atFirst = value.isNullOrBlank() || problem != null, init = change)
    }
}
